import { GridColDef, GridSortModel, GridCellParams, GridRowSelectionModel } from '@mui/x-data-grid';
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGridModel, DataGrid } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { Box, Button, Divider, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { AccountBalance, Clear, Description, HelpOutline, Send } from '@mui/icons-material';
import { BankIdIcon } from 'src/shared/components/atoms/bankIdIcon';
import { FixedFooter } from 'src/shared/components/navigations/fixedFooter';
import { getAllAutogiroMandate } from 'src/shared/services/autogiroMandateService';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { useGlobals } from 'src/shared/contexts/appContext';
import { PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { AutogiroMandateSourceEnum } from 'src/shared/models/enums/autogiroMandateSource.enum';
import { AutogiroMandateResponse } from '../models/autogiroMandateResponse';
import { AutogiroSearchFilter } from '../models/autogiroSearchFilter';
import { AutogiroMandateSendStatus } from './autogiroMandateSendStatus';
import { AutogiroMandateStatus } from './autogiroMandateStatus';
import { AutogiroReminderModal } from './autogiroReminderModal';
import { AutogiroMandateModal } from './autogiroMandateModal';
import { AutogiroSignedStatus } from './autogiroSignedStatus';

type AutogiroDataGridProps = {
  loadingCallback: (loading: boolean) => void;
  searchFilter: AutogiroSearchFilter;
  searchFilterCallback: (filter: AutogiroSearchFilter) => void;
};

export const AutogiroDataGrid = (props: AutogiroDataGridProps) => {
  const { loadingCallback, searchFilter, searchFilterCallback } = props;
  const { t } = useTranslation(['invoice']);
  const { dateStamp } = useGlobals();
  const { scrollIntoView, scrollRef } = useScrollIntoView();
  const isMounted = useIsMounted();

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<AutogiroMandateResponse>();

  const [rows, setRows] = useState<AutogiroMandateResponse[]>([]);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [reminderOpen, setReminderOpen] = useState<boolean>(false);
  const [data, setData] = React.useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: searchFilter.pageSize,
    page: searchFilter.pageIndex,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: searchFilter.sortCol, sort: searchFilter.sortDir }]);

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
  }, [searchFilter]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
    scrollIntoView();
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
      loadingCallback(true);
    }

    try {
      const result = await getAllAutogiroMandate(searchFilter);
      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);

        setTimeout(() => {
          ConsoleHelper.log(dateStamp);
          setRows(
            result.data.results.map((item) => {
              return { ...item, id: item.autogiroMandate_id };
            }),
          );
        }, 100);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
        loadingCallback(false);
      }
    }
  }, [isMounted, loadingCallback, searchFilter, dateStamp]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [searchFilter, fetchData, isMounted]);

  const onRowClick = (params) => {
    setSelectedItem(params.row);
    setModalOpen(true);
  };

  const columns: GridColDef[] = [
    {
      field: 'firstName',
      headerName: t('invoice:autogiro.tableHeader.firstName'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.firstName}</>;
      },
    },
    {
      field: 'lastName',
      headerName: t('invoice:autogiro.tableHeader.lastName'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.lastName}</>;
      },
    },
    {
      field: 'payerNr',
      headerName: t('invoice:autogiro.tableHeader.payerNr'),
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      type: 'number',
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.payerNr}</>;
      },
    },
    {
      field: 'personalIdentityNumber',
      headerName: t('invoice:autogiro.tableHeader.personalIdentityNumber'),
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      type: 'number',
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.personalIdentityNumber}</>;
      },
    },
    {
      field: 'createdAt',
      headerName: t('invoice:autogiro.tableHeader.createdAt'),
      sortable: true,
      flex: 1.25,
      renderCell: (params: GridCellParams) => {
        return <>{DateHelper.getDateTime(params.row.createdAt)}</>;
      },
    },
    {
      field: 'status',
      headerName: t('invoice:autogiro.tableHeader.status'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <AutogiroMandateStatus item={params.row} />;
      },
    },
    {
      field: 'isSigned',
      headerName: t('invoice:autogiro.tableHeader.isSigned'),
      sortable: true,
      type: 'number',
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        return <AutogiroSignedStatus isSigned={params.row.isSigned} />;
      },
    },
    {
      field: 'sent',
      headerName: t('invoice:autogiro.tableHeader.sent'),
      sortable: true,
      type: 'number',
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        return <AutogiroMandateSendStatus item={params.row} />;
      },
    },
    {
      field: 'linkedMemberName',
      headerName: t('invoice:autogiro.tableHeader.linkedTo'),
      sortable: false,
      flex: 1.5,
      renderCell: (params: GridCellParams) => {
        const hasMember = !!params.row.linkedMember_id;
        const hasDonor = !!params.row.linkedDonor_id;
        if (!hasMember && !hasDonor) return <>–</>;
        return (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
            {hasMember && (
              <Tooltip title={t('invoice:autogiro.tableHeader.member')}>
                <Link href={`${RoutesEnum.MemberDetails}/${params.row.linkedMember_id}`} onClick={(e) => e.stopPropagation()} underline='always'>
                  {params.row.linkedMemberName}
                </Link>
              </Tooltip>
            )}
            {hasMember && hasDonor && <span>,</span>}
            {hasDonor && (
              <Tooltip title={t('invoice:autogiro.tableHeader.donor')}>
                <Link href={`${RoutesEnum.DonorsDetail}/${params.row.linkedDonor_id}`} onClick={(e) => e.stopPropagation()} underline='always'>
                  {params.row.linkedDonorName}
                </Link>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'source',
      headerName: t('invoice:autogiro.labels.source'),
      sortable: true,
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        const sourceKey = AutogiroMandateSourceEnum[params.row.source ?? 0];
        const label = t(`invoice:autogiroMandateSource.${sourceKey}`);
        const iconProps = { fontSize: 'small' as const, sx: { color: 'text.secondary' } };
        let icon = <HelpOutline {...iconProps} />;
        if (params.row.source === AutogiroMandateSourceEnum.BankID) icon = <BankIdIcon {...iconProps} sx={{ color: '#183e4f' }} />;
        else if (params.row.source === AutogiroMandateSourceEnum.Bank) icon = <AccountBalance {...iconProps} />;
        else if (params.row.source === AutogiroMandateSourceEnum.Paper) icon = <Description {...iconProps} />;
        return <Tooltip title={label}>{icon}</Tooltip>;
      },
    },
    {
      field: 'autogiroMandate_id',
      headerName: t('fundraiser:tableHeader.dateCreated'),
      sortable: true,
      flex: 1.25,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.personalIdentityNumber}</>;
      },
    },
  ];

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          '& .MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
            py: 1,
          },
        }}
        ref={scrollRef}
      >
        <DataGrid<AutogiroMandateResponse>
          columns={columns}
          rows={rows}
          autoHeight
          dataModel={data}
          columnVisibilityModel={{
            autogiroMandate_id: false,
          }}
          checkboxSelection
          disableSelectionOnClick
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          sortModel={sortModel}
          onSortModelChange={(newSortModel) => {
            ConsoleHelper.log('newSortModel', newSortModel);
            const sort = sortModel[0].sort === 'asc' ? 'desc' : 'asc';
            if (newSortModel.length) {
              setSortModel(newSortModel);
            } else {
              setSortModel([{ field: sortModel[0].field, sort }]);
            }
            searchFilterCallback({
              ...searchFilter,
              pageIndex: 0,
              sortCol: newSortModel.length ? newSortModel[0].field : sortModel[0].field,
              sortDir: newSortModel.length ? newSortModel[0].sort : sort,
            });
          }}
          onRowClick={onRowClick}
          selectionModel={selectionModel}
          onSelectionModelChange={(newSelection) => setSelectionModel(newSelection)}
        />
      </Box>
      {selectionModel.length > 0 && (
        <FixedFooter>
          <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => setSelectionModel([])} sx={{ pr: 1 }}>
              <Clear fontSize='small' />
            </IconButton>
            <Typography variant='h6' sx={{ fontWeight: 400 }}>
              {t('common:statusMessages.footerSelectedCount', { count: selectionModel.length })}
            </Typography>
            <Divider orientation='vertical' flexItem sx={{ mx: 1.5 }} />
            <Button variant='contained' disableElevation endIcon={<Send fontSize='small' />} onClick={() => setReminderOpen(true)}>
              {t('invoice:autogiroReminder.footerButton')}
            </Button>
          </Box>
        </FixedFooter>
      )}
      {reminderOpen && (
        <AutogiroReminderModal
          modalOpen={reminderOpen}
          mandates={rows.filter((x) => selectionModel.indexOf(x.autogiroMandate_id) >= 0)}
          onClose={() => setReminderOpen(false)}
          onSent={() => {
            setReminderOpen(false);
            setSelectionModel([]);
          }}
        />
      )}
      {modalOpen && selectedItem && (
        <AutogiroMandateModal
          autogiroMandateId={selectedItem.autogiroMandate_id}
          onClose={() => setModalOpen(false)}
          onSave={() => {
            setModalOpen(false);
            fetchData();
          }}
          modalOpen={modalOpen}
        />
      )}
    </>
  );
};
